import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import { fetchHoleEvents, deleteHoleEvent, deleteAllHoleEvents } from '../adminApiService';
import toast from 'react-hot-toast';
import './HoleEventsTable.css';

const HoleEventsTable = ({ tournamentId, canModify = false }) => {
  const queryClient = useQueryClient();
  const [selectedEvents, setSelectedEvents] = useState(new Set());


  // Fetch hole events
  const { data: holeEventsData, isLoading, error, refetch } = useQuery({
    queryKey: ['hole-events', tournamentId],
    queryFn: () => fetchHoleEvents(tournamentId),
    enabled: !!tournamentId,
    refetchOnWindowFocus: false
  });

  // Delete single event mutation
  const deleteEventMutation = useMutation({
    mutationFn: ({ tournamentId, eventId }) => deleteHoleEvent(tournamentId, eventId),
    onSuccess: (data) => {
      toast.success(data.message);
      refetch();
      queryClient.invalidateQueries({ queryKey: ['hole-events', tournamentId] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete hole event');
    }
  });

  // Delete all events mutation
  const deleteAllEventsMutation = useMutation({
    mutationFn: (tournamentId) => deleteAllHoleEvents(tournamentId),
    onSuccess: (data) => {
      toast.success(data.message);
      refetch();
      queryClient.invalidateQueries({ queryKey: ['hole-events', tournamentId] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete hole events');
    }
  });

  const handleDeleteEvent = (eventId) => {
    if (!canModify) {
      toast.error('You do not have permission to delete hole events');
      return;
    }

    if (window.confirm('Are you sure you want to delete this hole event? This action cannot be undone.')) {
      deleteEventMutation.mutate({ tournamentId, eventId });
    }
  };

  const handleDeleteAllEvents = () => {
    if (!canModify) {
      toast.error('You do not have permission to delete hole events');
      return;
    }

    if (window.confirm('Are you sure you want to delete ALL hole events for this tournament? This action cannot be undone.')) {
      deleteAllEventsMutation.mutate(tournamentId);
    }
  };

  const handleRefresh = () => {
    refetch();
    toast.success('Hole events refreshed');
  };


  const getResultBadge = (result) => {
    const badgeClass = result === 'SKIN' ? 'result-skin' : 'result-saw-off';
    return <span className={`result-badge ${badgeClass}`}>{result}</span>;
  };

  const getScoreClass = (score, par) => {
    if (!par || isNaN(par)) return 'score-no-par';
    const diff = score - par;
    if (diff < 0) return 'score-under-par';
    if (diff === 0) return 'score-par';
    return 'score-over-par';
  };

  const formatScore = (score, par) => {
    if (!par || isNaN(par)) return `${score}`;
    const diff = score - par;
    if (diff < 0) return `${score} (${diff})`;
    if (diff === 0) return `${score} (E)`;
    return `${score} (+${diff})`;
  };

  if (isLoading) {
    return (
      <Card className="hole-events-loading">
        <div className="hole-events-loading-spinner">
          <div className="hole-events-spinner"></div>
          <p>Loading hole events...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="hole-events-error">
        <div className="error-content">
          <h3>Error Loading Hole Events</h3>
          <p>{error.message || 'Failed to load hole events'}</p>
          <Button onClick={handleRefresh} variant="primary">
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const { skins = [], sawOffs = [], total = 0 } = holeEventsData || {};
  
  // Debug: Check if tournamentTier is in the data
  if (skins.length > 0) {
    console.log('First skin golfer data:', skins[0].golfer);
    console.log('Has tournamentTier?', 'tournamentTier' in skins[0].golfer);
    console.log('TournamentTier value:', skins[0].golfer.tournamentTier);
  }

  return (
    <div className="hole-events-tables">
      {/* Header with actions */}
      <div className="hole-events-header">
        <div className="header-info">
          <h3>Hole Events</h3>
          <p>Total: {total} events ({skins.length} skins, {sawOffs.length} saw-offs)</p>
        </div>
        <div className="header-actions">
          <Button onClick={handleRefresh} variant="outline" size="small">
            🔄 Refresh
          </Button>
          {canModify && total > 0 && (
            <Button 
              onClick={handleDeleteAllEvents}
              variant="danger" 
              size="small"
              disabled={deleteAllEventsMutation.isPending}
            >
              {deleteAllEventsMutation.isPending ? 'Deleting...' : '🗑️ Delete All'}
            </Button>
          )}
        </div>
      </div>

      {/* Skins Table */}
      <Card className="skins-table-section">
        <div className="table-header">
          <h4>🏆 Skins ({skins.length})</h4>
          <p>Individual hole wins</p>
        </div>
        
        {skins.length === 0 ? (
          <div className="empty-state">
            <p>No skins events found</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="hole-events-table">
              <thead>
                <tr>
                  <th>Round</th>
                  <th>Hole</th>
                  <th>Player</th>
                  <th>Score</th>
                  <th>Par</th>
                  {canModify && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {skins.map((event) => (
                  <tr key={event.id}>
                    <td>R{event.round.roundNumber}</td>
                    <td>#{event.holeNumber}</td>
                    <td>
                      <div className="player-info">
                        <strong>{event.golfer.name}</strong>
                        <small>{event.golfer.tournamentTier || 'N/A'} • #{event.golfer.worldRanking || 'N/A'}</small>
                      </div>
                    </td>
                    <td className={getScoreClass(event.score, event.par)}>
                      {formatScore(event.score, event.par)}
                    </td>
                    <td>{event.par}</td>
                    {canModify && (
                      <td>
                        <Button
                          onClick={() => handleDeleteEvent(event.id)}
                          variant="danger"
                          size="small"
                          disabled={deleteEventMutation.isPending}
                          title="Delete this hole event"
                        >
                          🗑️ Delete
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Saw-offs Table */}
      <Card className="saw-offs-table-section">
        <div className="table-header">
          <h4>⚔️ Saw-offs ({sawOffs.length})</h4>
          <p>Hole ties with shared prizes</p>
        </div>
        
        {sawOffs.length === 0 ? (
          <div className="empty-state">
            <p>No saw-off events found</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="hole-events-table">
              <thead>
                <tr>
                  <th>Round</th>
                  <th>Hole</th>
                  <th>Player</th>
                  <th>Score</th>
                  <th>Par</th>
                  {canModify && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sawOffs.map((event) => (
                  <tr key={event.id}>
                    <td>R{event.round.roundNumber}</td>
                    <td>#{event.holeNumber}</td>
                    <td>
                      <div className="player-info">
                        <strong>{event.golfer.name}</strong>
                        <small>{event.golfer.tournamentTier || 'N/A'} • #{event.golfer.worldRanking || 'N/A'}</small>
                      </div>
                    </td>
                    <td className={getScoreClass(event.score, event.par)}>
                      {formatScore(event.score, event.par)}
                    </td>
                    <td>{event.par}</td>
                    {canModify && (
                      <td>
                        <Button
                          onClick={() => handleDeleteEvent(event.id)}
                          variant="danger"
                          size="small"
                          disabled={deleteEventMutation.isPending}
                          title="Delete this saw-off (both players will be removed)"
                        >
                          🗑️ Delete
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Summary Stats */}
      {total > 0 && (
        <Card className="hole-events-summary">
          <h4>Summary</h4>
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Total Skins:</span>
              <span className="stat-value">{skins.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Saw-offs:</span>
              <span className="stat-value">{sawOffs.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Events:</span>
              <span className="stat-value">{total}</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default HoleEventsTable;
